import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../shared/states/appstate';
import * as authActions from './auth.actions';
import {
	selectToken,
	selectRefreshToken,
	selectAnonymousToken,
	selectIsAnonymous,
	selectLoading,
	selectCurrentUser,
	selectLocation,
	selectLockAnonymousFeatures,
	selectError
} from './auth.selectors';
import { LoginResponse } from '../auth.models';
import { Observable } from 'rxjs/internal/Observable';
import { StateError } from '../../shared/state';
import { take } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AuthFacade {

	public currentUser$ = this.store.pipe(select(selectCurrentUser));
	public isAnonymous$ = this.store.pipe(select(selectIsAnonymous));
	public token$ = this.store.pipe(select(selectToken));
	public refreshToken$ = this.store.pipe(select(selectRefreshToken));
	public anonymousToken$ = this.store.pipe(select(selectAnonymousToken));
	public loading$ = this.store.pipe(select(selectLoading));
	public location$ = this.store.pipe(select(selectLocation));
	public isLocked$ = this.store.pipe(select(selectLockAnonymousFeatures));
	public readonly error$: Observable<StateError> = this.store.select(selectError);

	constructor(private store: Store<AppState>) {

	}

	loadCurrentUser() {
		this.store.dispatch(new authActions.LoadAction());
	}

	// ID-5: logging in happens at Pro Pool ID.
	login() {
		this.store.dispatch(new authActions.LoginAction());
	}

	// Pro Pool ID's redirect back to the app, with the authorization code.
	completeLogin(callbackUrl: string) {
		this.store.dispatch(new authActions.LoginCallbackAction(callbackUrl));
	}

	tokensRefreshed(response: LoginResponse) {
		this.store.dispatch(new authActions.TokenRefreshedAction(response));
	}

	signedOut() {
		this.store.dispatch(new authActions.SignedOutAction());
	}

	// ID-6B: an account is created at Pro Pool ID, never in the app; the new account comes back signed in.
	register() {
		this.store.dispatch(new authActions.LoginAction(true));
	}

	loadStorage() {
		this.store.dispatch(new authActions.LoadStorageAction());
	}

	logout() {
		this.store.dispatch(new authActions.LogoutAction());
	}

	// ID-10: call this when Pro Pool ID's profile screen closes and when the app is foregrounded again.
	refreshProfile() {
		this.store.dispatch(new authActions.RefreshProfileAction());
	}

	updateCoordinates() {
		this.store.dispatch(new authActions.UpdateCoordinatesAction());
	}

	// A known position from elsewhere than the device GPS (CheckinGeofenceService's geofence entry).
	setCoordinates(latitude: number, longitude: number) {
		this.store.dispatch(new authActions.UpdateCoordinatesOkAction(latitude, longitude));
	}
}
